import { z } from 'zod';
import {
  COGNITO_GROUPS,
  EMIRATES,
  EMPLOYMENT_TYPES,
  JOB_PRIORITIES,
  JOB_STATUSES,
  POST_STATUSES,
  SERVICE_TYPES,
} from './enums.js';

/** UAE phone number. Allow +971 with 8 or 9 digits after country code. */
export const uaePhoneSchema = z
  .string()
  .regex(/^\+971[0-9]{8,9}$/, 'Must be a UAE phone number (+971…)');

export const emailSchema = z.string().email().toLowerCase().trim();

/** Common ID schemas. ULIDs are used everywhere; Cognito sub is a UUID. */
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Invalid ULID');
export const uuidSchema = z.string().uuid();

/** Address fields used across customer, job, and worker home addresses. */
export const addressSchema = z.object({
  building: z.string().min(1).max(120).optional(),
  unit: z.string().max(60).optional(),
  street: z.string().max(160).optional(),
  area: z.string().min(1).max(120),
  emirate: z.enum(EMIRATES),
  makaniNumber: z.string().regex(/^\d{8}$|^\d{4}-\d{4}$/).optional(),
  plusCode: z.string().max(40).optional(),
  geo: z
    .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
    .optional(),
});

export const customerCreateSchema = z.object({
  name: z.string().min(2).max(120),
  phone: uaePhoneSchema,
  whatsappPhone: uaePhoneSchema.optional(),
  email: emailSchema.optional(),
  address: addressSchema,
  notes: z.string().max(2000).optional(),
});
export type CustomerCreate = z.infer<typeof customerCreateSchema>;

export const workerCreateSchema = z.object({
  name: z.string().min(2).max(120),
  phone: uaePhoneSchema,
  email: emailSchema,
  employmentType: z.enum(EMPLOYMENT_TYPES),
  services: z.array(z.enum(SERVICE_TYPES)).min(1),
  homeArea: z.string().max(120).optional(),
  active: z.boolean().default(true),
  /** Cognito `sub` (UUID) — set when admin pre-provisions a Cognito user for this worker. */
  cognitoSub: uuidSchema.optional(),
});
export type WorkerCreate = z.infer<typeof workerCreateSchema>;

export const jobCreateSchema = z.object({
  customerId: ulidSchema,
  serviceType: z.enum(SERVICE_TYPES),
  priority: z.enum(JOB_PRIORITIES).default('normal'),
  scheduledAt: z.string().datetime(),
  /** When true the job is booked for the day with no committed time slot.
   *  scheduledAt still anchors it to a day (Dubai) for sorting/calendar. */
  anytime: z.boolean().default(false),
  estimatedDurationMinutes: z.number().int().min(15).max(720).default(60),
  description: z.string().max(4000).optional(),
  internalNotes: z.string().max(4000).optional(),
  /** Free-text notes the assigned worker can write from the field. */
  workerNotes: z.string().max(4000).optional(),
  /** Single-worker assignment (deprecated; use assignedWorkerIds). Kept so
   *  older clients can still create jobs. Server-side it's normalised into
   *  assignedWorkerIds[0]. */
  assignedWorkerId: ulidSchema.optional(),
  /** Multi-worker assignment (max 5). The first id is the primary worker
   *  (used for GSI2 dispatch + WhatsApp templates). */
  assignedWorkerIds: z.array(ulidSchema).max(5).optional(),
});
export type JobCreate = z.infer<typeof jobCreateSchema>;

export const jobStatusUpdateSchema = z.object({
  status: z.enum(JOB_STATUSES),
  reason: z.string().max(1000).optional(),
});

/**
 * Invoice creation. Money is stored in **fils** (1 AED = 100 fils) to avoid
 * floating-point rounding bugs. VAT is applied at 5% on the line subtotal.
 */
export const invoiceLineItemSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().min(0.01).max(10_000),
  unitPriceFils: z.number().int().min(0).max(100_000_000),
});
export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;

export const invoiceCreateSchema = z.object({
  items: z.array(invoiceLineItemSchema).min(1).max(50),
  notes: z.string().max(1000).optional(),
  /** Override default VAT rate of 0.05 (5%). Stored as a fraction. */
  vatRate: z.number().min(0).max(1).default(0.05),
});
export type InvoiceCreate = z.infer<typeof invoiceCreateSchema>;

/**
 * Worker acknowledgement of an overdue job. Worker tells dispatch "I see it,
 * I'm running late by X minutes." Suppresses further overdue pings and
 * surfaces a green check on the dispatch board.
 */
export const overdueAckSchema = z.object({
  etaMinutes: z.number().int().min(1).max(240),
  note: z.string().max(500).optional(),
});
export type OverdueAck = z.infer<typeof overdueAckSchema>;

/** JWT claims we trust from the API Gateway Cognito authorizer. */
export const jwtClaimsSchema = z.object({
  sub: uuidSchema,
  email: emailSchema.optional(),
  phone_number: uaePhoneSchema.optional(),
  'cognito:groups': z
    .union([z.array(z.string()), z.string(), z.undefined()])
    .transform((g) => {
      if (!g) return [] as readonly typeof COGNITO_GROUPS[number][];
      // API Gateway JWT authorizer flattens arrays into a string like "[super_admin admin]".
      // Cognito ID-token JSON is an array. Handle both, plus comma-separated.
      const tokens = Array.isArray(g)
        ? g
        : g.replace(/^\[|\]$/g, '').split(/[\s,]+/);
      return tokens.filter((x) => (COGNITO_GROUPS as readonly string[]).includes(x)) as readonly typeof COGNITO_GROUPS[number][];
    }),
});
export type JwtClaims = z.infer<typeof jwtClaimsSchema>;

/**
 * R7 — public customer feedback. Submitted unauthenticated from
 * feedback.fixitninjaz.com after a job completes.
 *  - `source: 'google'` is logged when the customer clicks the Google review CTA
 *    (the actual review lands on Google Maps; we just record intent).
 *  - `source: 'private'` is the inline 1–5 star + comment form.
 */
export const FEEDBACK_SOURCES = ['google', 'private'] as const;
export type FeedbackSource = (typeof FEEDBACK_SOURCES)[number];

export const feedbackCreateSchema = z.object({
  jobId: ulidSchema,
  source: z.enum(FEEDBACK_SOURCES),
  rating: z.number().int().min(1).max(5).optional(),
  comment: z.string().max(2000).optional(),
  /** Free-text contact preference (phone/email) for follow-ups on bad reviews. */
  contact: z.string().max(160).optional(),
});
export type FeedbackCreate = z.infer<typeof feedbackCreateSchema>;

/** Minimal job summary returned by `GET /public/jobs/{id}` for the feedback page. */
export const publicJobSummarySchema = z.object({
  id: ulidSchema,
  serviceType: z.enum(SERVICE_TYPES),
  status: z.enum(JOB_STATUSES),
  scheduledAt: z.string().datetime(),
  workerName: z.string().optional(),
});
export type PublicJobSummary = z.infer<typeof publicJobSummarySchema>;

// ── Blog content (Phase 3) ───────────────────────────────────────────────────

/**
 * Slug format: 3–80 chars, lowercase letters/digits, single hyphens between
 * segments. Reserved-set check happens server-side (see @fin/shared/reserved-slugs).
 */
export const blogSlugSchema = z
  .string()
  .min(3)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase a-z, 0-9, single hyphens');

export const heroImageSchema = z.object({
  src: z.string().min(1).max(400),
  alt: z.string().min(1).max(300),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

/** Body of POST /v1/content/posts — creates a draft. */
export const postCreateSchema = z.object({
  slug: blogSlugSchema,
  title: z.string().min(3).max(200),
  description: z.string().min(20).max(300),
  category: z.string().min(1).max(80).optional(),
  tags: z.array(z.string().min(1).max(60)).max(20).optional(),
  focusKeyword: z.string().max(120).optional(),
  secondaryKeywords: z.array(z.string().max(120)).max(20).optional(),
  author: z.string().max(120).optional(),
  hero: heroImageSchema.optional(),
  /** Raw markdown body (without frontmatter). Persisted to S3 as post.md. */
  body: z.string().max(200_000).default(''),
});
export type PostCreate = z.infer<typeof postCreateSchema>;

/**
 * Body of PUT /v1/content/posts/{id} — used for autosave + full saves.
 * All fields optional; whatever is sent gets merged. `status` lets the
 * editor transition draft↔scheduled (publish is a separate endpoint).
 */
export const postUpdateSchema = z.object({
  slug: blogSlugSchema.optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(300).optional(),
  category: z.string().max(80).optional(),
  tags: z.array(z.string().max(60)).max(20).optional(),
  focusKeyword: z.string().max(120).optional(),
  secondaryKeywords: z.array(z.string().max(120)).max(20).optional(),
  author: z.string().max(120).optional(),
  hero: heroImageSchema.nullable().optional(),
  body: z.string().max(200_000).optional(),
  status: z.enum(POST_STATUSES).optional(),
  publishAt: z.string().datetime().optional(),
});
export type PostUpdate = z.infer<typeof postUpdateSchema>;

/**
 * Body of POST /v1/content/posts/{id}/preview — renders in memory, no S3 write.
 * Defaults to the saved version on S3 when body/title/etc. are omitted.
 */
export const postPreviewSchema = z.object({
  title: z.string().max(200).optional(),
  description: z.string().max(300).optional(),
  category: z.string().max(80).optional(),
  tags: z.array(z.string().max(60)).max(20).optional(),
  focusKeyword: z.string().max(120).optional(),
  hero: heroImageSchema.nullable().optional(),
  body: z.string().max(200_000).optional(),
});
export type PostPreview = z.infer<typeof postPreviewSchema>;

/** Body of POST /v1/content/posts/{id}/restore. */
export const postRestoreSchema = z.object({
  versionId: z.string().min(1).max(200),
});
export type PostRestore = z.infer<typeof postRestoreSchema>;

